import { assign, createMachine } from "xstate";
import {
  fetchConsentsCatalogue,
  IConsent,
} from "@nju/data-access-portal-consents";
import { IOfferMode } from "@nju/data-access-portal-offers";
import { setConsents } from "@nju/data-access-portal-order";

export interface IContext {
  consentsCatalogue: (IConsent & { accepted?: boolean })[];
  mode?: IOfferMode | "multiOrder";
  orchestratorId?: string;
}

type ISelectConsentEventPayload = {
  consentId: string;
  consentVersion: number;
  accepted: boolean;
};

type ISelectConsentEvent = {
  type: "SELECT_CONSENT";
  consent: ISelectConsentEventPayload;
};

type ISelectAllConsentsEvent = {
  type: "SELECT_ALL_CONSENTS";
  value: boolean;
};

type IEvent =
  | {
      type: "CONSENTS_LOADED";
      consentsCatalogue: IConsent[];
    }
  | {
      type: "FETCH_CONSENTS_ERROR";
      error: unknown;
    }
  | {
      type: "SUBMIT";
    }
  | {
      type: "SET_CONSENTS_ERROR";
    }
  | {
      type: "SET_CONSENTS_SUCCESS";
    }
  | ISelectConsentEvent
  | ISelectAllConsentsEvent;

export const consentsMachine = createMachine(
  {
    schema: {
      context: {
        consentsCatalogue: [],
      } as IContext,
      events: {} as IEvent,
    },
    initial: "init",
    states: {
      init: {
        invoke: {
          src: "fetchData",
        },
        on: {
          CONSENTS_LOADED: [{ actions: "setData" }],
          SUBMIT: [{ target: "submitting" }],
        },
      },
      submitting: {
        invoke: {
          src: "setConsents",
        },
        on: {
          SET_CONSENTS_SUCCESS: "setConsentsSuccess",
          SET_CONSENTS_ERROR: "setConsentsError",
        },
      },
      setConsentsSuccess: {
        entry: "redirectToNextStep",
      },
      setConsentsError: {
        entry: "redirectToMyAccount",
      },
    },
    on: {
      SELECT_CONSENT: {
        actions: assign<IContext, ISelectConsentEvent>((context, event) => {
          return {
            ...context,
            consentsCatalogue: context.consentsCatalogue.map((consent) => {
              return consent.id === event.consent.consentId
                ? { ...consent, accepted: event.consent.accepted }
                : consent;
            }),
          };
        }),
      },
      SELECT_ALL_CONSENTS: {
        actions: assign<IContext, ISelectAllConsentsEvent>((context, event) => {
          return {
            ...context,
            consentsCatalogue: context.consentsCatalogue.map((c) => {
              return {
                ...c,
                accepted: event.value,
              };
            }),
          };
        }),
      },
    },
  },
  {
    actions: {
      setData: assign<IContext, IEvent>((context, event) => {
        if (event.type === "CONSENTS_LOADED") {
          return {
            consentsCatalogue: event.consentsCatalogue,
          };
        }
        return context;
      }),
    },
    services: {
      fetchData: () => {
        return async (callback) => {
          try {
            const consentsCatalogue = await fetchConsentsCatalogue();
            callback({
              type: "CONSENTS_LOADED",
              consentsCatalogue: consentsCatalogue.unwrap(),
            });
          } catch (error) {
            callback({ type: "FETCH_CONSENTS_ERROR", error });
          }
        };
      },
      setConsents: ({ mode, orchestratorId, consentsCatalogue }) => {
        return async (callback) => {
          if (!orchestratorId || !mode) {
            return callback({ type: "SET_CONSENTS_ERROR" });
          }

          const resolvedMode = mode === "mobile" ? "multiOrder" : mode;

          const result = await setConsents({
            mode: resolvedMode,
            consents: consentsCatalogue.map(
              ({ id, accepted, consentVersion }) => {
                return {
                  consentId: id,
                  accepted: !!accepted,
                  consentVersion,
                };
              },
            ),
            orchestratorId,
          });

          if (result.isError) {
            return callback({ type: "SET_CONSENTS_ERROR" });
          }

          return callback({ type: "SET_CONSENTS_SUCCESS" });
        };
      },
    },
  },
);
